// Where "Go to my dashboard" lands in the mock (Figma 1:5046, "Welcome back").
//
// The real destination is the existing actor dashboard on another domain, and
// that handoff — the JWT/cookie exchange across domains — is still unowned
// (docs/13). Rather than end the demo on a dead link or bounce someone out to
// thecwbint.com mid-walkthrough, the mock lands here and says so.

import React, { useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import { isMockApi } from "../../../shared/api/submission";
import { dashboardUrl } from "../common/dashboard";
import { trackSubmissionEvent } from "../analytics";

interface Props extends StateProps {
  history: any;
  location: any;
}

const MockDashboardPage: React.FC<Props> = ({ history, firstName, sent }) => {
  useEffect(() => {
    if (!isMockApi()) {
      // A real build has a real dashboard, elsewhere.
      window.location.replace(dashboardUrl());
      return;
    }
    trackSubmissionEvent("sf_step4_mock_dashboard_viewed", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SubmissionLayout>
      <Centered>
        <Title>
          {firstName
            ? i18next.t("Welcome, {{name}}!", { name: firstName })
            : i18next.t("Welcome!")}
        </Title>
        {sent && (
          <Card>
            <CardLabel>{i18next.t("Pending submission")}</CardLabel>
            <CardName>{sent.breakdownName}</CardName>
            {sent.roleName && <CardRole>{sent.roleName}</CardRole>}
          </Card>
        )}
        <Note>
          {i18next.t(
            "The real dashboard lives outside this app. In the mock, this page stands in for it."
          )}
        </Note>
        <TextButton onClick={() => history.push("/submission/casting-calls")}>
          {i18next.t("Back to casting calls")}
        </TextButton>
      </Centered>
    </SubmissionLayout>
  );
};

const Centered = styled.div`
  text-align: center;
  padding: 32px 0 8px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 18px;
`;

const Card = styled.div`
  border: 1px solid #e3e8ee;
  border-radius: 10px;
  padding: 14px;
  text-align: left;
  margin-bottom: 18px;
`;

const CardLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #53627c;
`;

const CardName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #122640;
  margin-top: 4px;
`;

const CardRole = styled.div`
  font-size: 13px;
  color: #53627c;
`;

const Note = styled.div`
  font-size: 12px;
  color: #8a94a6;
  line-height: 18px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 0;
  cursor: pointer;
  min-height: 44px;
`;

interface StateProps {
  firstName: string;
  sent: iRootState["submissionStep4Model"]["sent"];
}

const mapStateToProps = (state: iRootState): StateProps => {
  // The flow stores the name on the review payload rather than in signup state,
  // so the greeting comes from there — first word only.
  const review = state.submissionStep4Model.review;
  const actorName = review ? review.actorName : "";
  return {
    firstName: actorName ? actorName.split(" ")[0] : "",
    sent: state.submissionStep4Model.sent,
  };
};

export default connect(mapStateToProps)(MockDashboardPage);
